// WhatsApp offers consent for the customer on the floor bill. When a named
// customer is picked (online) their saved answer is read and pre-set; a change
// is saved to the customer straight away. Offline nothing can be saved, so the
// tick is kept for this bill only and the cashier is told it needs saving
// later. status: "saved" (the server has this answer), "unsaved" (changed but
// not saved), or "unknown" (never asked, or couldn't check).
import { useCallback, useEffect, useRef, useState } from "react";
import { getFashionConsent, setFashionConsent } from "../../../../services";

export const OFFLINE_CONSENT_MSG = "You're offline, so this isn't saved to the customer. Note it at the till and save it once back online.";

export default function useFloorConsent({ customerId, named, bunitId, isOnline, say }) {
  const online = isOnline !== false;
  const [state, setState] = useState({ value: false, status: "unknown", busy: false });
  const call = useRef(0);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  // A new customer starts unticked; their saved answer is read when online.
  useEffect(() => {
    const id = ++call.current;
    if (!named || !customerId || !online) {
      setState({ value: false, status: "unknown", busy: false });
      return;
    }
    setState({ value: false, status: "unknown", busy: true });
    getFashionConsent(customerId)
      .catch(() => ({ ok: false, whatsappOffers: null }))
      .then((res) => {
        if (!alive.current || id !== call.current) return;
        const known = res?.ok && res.whatsappOffers !== null;
        setState({ value: res?.whatsappOffers === true, status: known ? "saved" : "unknown", busy: false });
      });
  }, [customerId, named, online]);

  const change = useCallback(
    async (checked) => {
      if (state.busy || !customerId) return;
      if (!online) {
        setState({ value: checked, status: "unsaved", busy: false });
        say?.(OFFLINE_CONSENT_MSG, "warn");
        return;
      }
      const before = state;
      const id = ++call.current;
      setState({ value: checked, status: before.status, busy: true });
      let res;
      try {
        res = await setFashionConsent(customerId, bunitId, checked);
      } catch {
        res = { ok: false, message: "Couldn't save it. Try again." };
      }
      if (!alive.current || id !== call.current) return;
      if (res?.ok) {
        setState({ value: checked, status: "saved", busy: false });
        say?.(res.message || (checked ? "Will get offers on WhatsApp." : "No offers on WhatsApp."), "ok");
      } else {
        // Not saved: put the tick back to what the customer has.
        setState({ ...before, busy: false });
        say?.(res?.message || "Couldn't save it. Try again.", "bad");
      }
    },
    [state, customerId, bunitId, online, say]
  );

  return { consent: state.value, consentStatus: state.status, consentBusy: state.busy, onConsent: change };
}
